import React from 'react';
import { useStyletron } from 'baseui';
import { useAtomValue } from 'jotai';
import type { AnnotationLabelT, RangeSpanT } from '../../../../../../../common/types/types';
import { annotationPropagationMethod } from '../../../../../../labelling-v2/atoms/frame-timeline-atoms';
import { keyframeStepperTrackingEnabledAtom } from '../../../../../../labelling-v2/atoms/ml-tracking-atoms';
import type { TimeDisplayMode } from '../../../../../../types';
import type { PROPAGATION_METHOD } from '../../../../../reducers/actions';
import { ACTION_TYPE } from '../../../constants';
import { calculateMaxFrames, isTrackingActiveTab } from '../../utils';
import { AddNewSequence } from '../add-new-sequence';
import { type TimelineTab, AnnotationTimelineSelect } from '../annotation-timeline-select';
import { KeyframeStepperSequence } from '../keyframe-stepper-sequence';
const TIMELINE_TABS = ({
  TRACKING: {
    id: '0',
    title: 'Tracking'
  },
  INTERPOLATION: {
    id: '1',
    title: 'Interpolation'
  }
} as const);
type PropsT = {
  annotationId: string;
  annotationLabel: AnnotationLabelT;
  currentFrame: number;
  maxFrames: number;
  hasManualKeyFrameInRange: (start: number, end: number) => boolean;
  annotationSequence: Map<string, RangeSpanT>;
  onPropagationMethodChange: (propagationMethod: (typeof PROPAGATION_METHOD)[keyof typeof PROPAGATION_METHOD]) => void;
  onChange: (annotationSequence: Map<string, RangeSpanT>, isAnnotationTracking?: boolean) => Promise<void>;
  actionConfig?: string[];
  setTrackedSequence?: (sequence: {
    start: number;
    end: number;
    annotationId: string;
  } | null) => void;
  timeDisplayMode: TimeDisplayMode;
  frameRate: number | null | undefined;
  onClose?: () => void;
};
export const AddSequenceSection = (props: PropsT) => {
  const [css, theme] = useStyletron();
  const propagationMethod = useAtomValue(annotationPropagationMethod);
  const isKeyframeStepperEnabled = useAtomValue(keyframeStepperTrackingEnabledAtom);
  const hasTracking = props.actionConfig?.some(action => action === ACTION_TYPE.TRACK || action === ACTION_TYPE.TRACK_30);
  const hasTrack30 = props.actionConfig?.includes(ACTION_TYPE.TRACK_30);
  const hasInterpolation = props.actionConfig?.includes(ACTION_TYPE.INTERPOLATION);
  const activeTabId = isTrackingActiveTab(propagationMethod, Boolean(hasTracking)) ? TIMELINE_TABS.TRACKING.id : TIMELINE_TABS.INTERPOLATION.id;

  // Determine available tabs based on actionConfig
  const availableTabs: TimelineTab[] = [];
  if (hasTracking) {
    availableTabs.push(TIMELINE_TABS.TRACKING);
  }
  if (hasInterpolation) {
    availableTabs.push(TIMELINE_TABS.INTERPOLATION);
  }
  const maxFrames = calculateMaxFrames({
    isTrackingMode: Boolean(hasTracking) && activeTabId === TIMELINE_TABS.TRACKING.id,
    hasInterpolation: Boolean(hasInterpolation),
    hasTrack30: Boolean(hasTrack30),
    currentFrame: props.currentFrame,
    maxFrames: props.maxFrames
  });
  const commonProps = {
    annotationId: props.annotationId,
    annotationLabel: props.annotationLabel,
    currentFrame: props.currentFrame,
    maxFrames,
    onChange: props.onChange,
    annotationSequence: props.annotationSequence,
    setTrackedSequence: props.setTrackedSequence,
    hasManualKeyFrameInRange: props.hasManualKeyFrameInRange,
    timeDisplayMode: props.timeDisplayMode,
    frameRate: props.frameRate,
    totalFrames: props.maxFrames,
    onClose: props.onClose
  };

  // Rectangle TRACK only: segmentation (TRACK_30) and interpolation keep the legacy form.
  const canUseStepper = isKeyframeStepperEnabled && Boolean(hasTracking) && !hasTrack30;
  const renderTrackingForm = (actionConfig: string[]) => canUseStepper && actionConfig.includes(ACTION_TYPE.TRACK) ?
  // Stepper bounds the whole track window → pass the full-video maxFrames, not the capped one.
  <KeyframeStepperSequence {...commonProps} maxFrames={props.maxFrames} actionConfig={actionConfig} /> : <AddNewSequence {...commonProps} actionConfig={actionConfig} />;
  return <>
      <div className={css({
      fontSize: theme.typography.LabelSmall.fontSize,
      color: theme.colors.contentSecondary,
      paddingTop: theme.sizing.scale400,
      paddingBottom: theme.sizing.scale100,
      paddingLeft: theme.sizing.scale0,
      margin: 0
    })}>
        Add New Sequence
      </div>

      {/* Single mode */}
      {(!hasTracking || !hasInterpolation) && <div className={css({
      overflow: 'auto'
    })}>
          {renderTrackingForm(hasInterpolation ? [ACTION_TYPE.INTERPOLATION] : [ACTION_TYPE.TRACK])}
        </div>}

      {/* Multiple modes with tabs */}
      {hasTracking && hasInterpolation && <>
          <AnnotationTimelineSelect options={availableTabs} activeTabId={activeTabId} onTabChange={props.onPropagationMethodChange} />
          <div className={css({
        overflow: 'auto'
      })}>
            {renderTrackingForm([activeTabId === TIMELINE_TABS.TRACKING.id ? ACTION_TYPE.TRACK : ACTION_TYPE.INTERPOLATION])}
          </div>
        </>}
    </>;
};